import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import { styled } from "@mui/material/styles";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";

import { useDispatch, useSelector } from "react-redux";
import { find, uniq } from "lodash";
import { Button, Tooltip } from "@material-ui/core";
import { updateSubscriptionItems } from "redux/action/customer.action";
import { useSnackbar } from "notistack";
import { getCustomer } from "redux/action/customer.action";
import { HelpOutline } from "@material-ui/icons";
import { buildImageSlides, getFirstSlideUrl } from "utils/imageSlides";
import ImageCarouselDialog from "components/ImageCarouselDialog";

const useStyles = makeStyles(theme => ({
  table: {
    minWidth: 700
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px 15px 15px 30px",
    borderBottom: "1px solid lightgrey"
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0px 30px 30px 0px"
  },
  title: {
    display: "flex",
    alignItems: "center",
    fontWeight: "500",
    fontSize: "27px",
    lineHeight: "1.23"
  },
  searchContainer: {
    padding: "15px 30px",
    borderBottom: "1px solid lightgrey"
  },
  thumbnail: {
    width: "30px",
    height: "30px",
    objectFit: "cover",
    cursor: "pointer",
    borderRadius: "4px",
    border: "1px solid #ddd"
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  "&.MuiTableCell-body": {
    fontSize: 14
  }
}));

export default function ProductItems() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [changeItems, setChangeItems] = useState({});
  const [itemsEditable, setitemsEditable] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const CUSTOMER_STATE = useSelector(state => state.customer);
  const subscriptionItems = CUSTOMER_STATE.customer?.subscription_items ?? [];
  const subscriptionProducts = CUSTOMER_STATE.customer?.subscription_products ?? [];

  const productIds = uniq(subscriptionProducts.map(product => product._id));

  const getProductSortKey = item => {
    const productId = item.product_id?._id ?? "";
    const name = item.product_id?.product_type_id?.name ?? find(subscriptionProducts, p => p._id === productId)?.product_type_id?.name ?? "";
    return `${productId}, ${name}`;
  };

  const handleChangeProduct = ({ itemId, currentProductId, newProductId }) => {
    if (currentProductId === newProductId) {
      delete changeItems[itemId];
      setChangeItems({ ...changeItems });
    } else {
      setChangeItems({ ...changeItems, [itemId]: { itemId, currentProductId, newProductId } });
    }
  };

  const getProductIdWithName = productId => {
    const product = find(subscriptionProducts, product => product._id === productId);
    return `${product._id}, ${product.product_type_id.name}`;
  };

  const handleupdateCustomerSuccess = () => {
    enqueueSnackbar(t("itemLists.itemsUpdated"), { variant: "success" });
    dispatch(getCustomer({ id: CUSTOMER_STATE.customer._id }));
  };

  const handleupdateCustomerFailure = error => {
    enqueueSnackbar(error, { variant: "error" });
  };

  const saveItems = () => {
    dispatch(
      updateSubscriptionItems(
        { items: Object.values(changeItems), subscriptionId: CUSTOMER_STATE.customer.stripe_subscription_id },
        handleupdateCustomerSuccess,
        handleupdateCustomerFailure
      )
    );
  };

  const defectLabel = t("itemLists.defect");

  const handleImageClick = row => {
    const slides = buildImageSlides(row, defectLabel);
    if (slides.length) {
      setSelectedSlides(slides);
      setSlideIndex(0);
      setImageModalOpen(true);
    }
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedSlides([]);
    setSlideIndex(0);
  };

  const filteredItems = subscriptionItems
    .filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item._id?.toLowerCase().includes(query) ||
        item.content_name?.toLowerCase().includes(query) ||
        item.order_id?.toLowerCase().includes(query) ||
        item.return_id?.toLowerCase().includes(query) ||
        item.product_id?._id?.toLowerCase().includes(query) ||
        item.product_id?.product_type_id?.name?.toLowerCase().includes(query) ||
        item.sub_items?.some(subItem => subItem.barcode_id?.toLowerCase().includes(query))
      );
    })
    .sort((a, b) => getProductSortKey(a).localeCompare(getProductSortKey(b)));
  return (
    <React.Fragment>
      <div className={classes.header}>
        <div style={{ display: "flex" }}>
          <div className={classes.title}>{t("itemLists.items")}</div>
          <Tooltip title={t("itemLists.itemsTooltip")}>
            <IconButton>
              <HelpOutline />
            </IconButton>
          </Tooltip>
        </div>
        <Button color="primary" variant="outlined" onClick={() => setitemsEditable(true)}>
          {t("itemLists.edit")}
        </Button>
      </div>
      <div className={classes.searchContainer}>
        <TextField variant="outlined" placeholder={t("common.search")} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} size="small" style={{ width: "300px" }} />
      </div>
      <div className="table">
        <TableContainer component={Paper}>
          <Table aria-label="customized table" size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">{t("itemLists.itemImage")}</StyledTableCell>
                <StyledTableCell>{t("itemLists.id")}</StyledTableCell>
                <StyledTableCell align="center">{t("itemLists.contentName")}</StyledTableCell>
                <StyledTableCell align="center">{t("itemLists.quantity")}</StyledTableCell>
                <StyledTableCell align="center">{t("itemLists.barcodeIds")}</StyledTableCell>
                <StyledTableCell align="center">{t("itemLists.product")}</StyledTableCell>
                <StyledTableCell align="center">{t("itemLists.orderId")}</StyledTableCell>
                <StyledTableCell align="center">{t("itemLists.returnId")}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map(row => (
                <TableRow key={row._id}>
                  <TableCell align="center">
                    {getFirstSlideUrl(row, defectLabel) ? (
                      <img
                        src={getFirstSlideUrl(row, defectLabel)}
                        alt="Item"
                        className={classes.thumbnail}
                        onClick={() => handleImageClick(row)}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row._id}
                  </TableCell>
                  <TableCell>{row.content_name}</TableCell>
                  <TableCell align="center">{row.quantity}</TableCell>
                  <TableCell align="center">
                    {row?.sub_items?.filter(sub_item => sub_item.barcode_id).length > 0
                      ? row.sub_items
                          .filter(sub_item => sub_item.barcode_id)
                          .map((sub_item, index) => (
                            <div key={index}>
                              <a href={sub_item.barcode_image_url} target="_blank" rel="noopener noreferrer">
                                {sub_item.barcode_id}
                              </a>
                            </div>
                          ))
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {itemsEditable ? (
                      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <Select
                          labelId="demo-select-small"
                          id="demo-select-small"
                          value={changeItems[row._id]?.newProductId || row.product_id._id}
                          label="Id, Name"
                          onChange={event => handleChangeProduct({ itemId: row._id, currentProductId: row.product_id._id, newProductId: event.target.value })}
                        >
                          {productIds.map(productId => (
                            <MenuItem value={productId}>{getProductIdWithName(productId)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <>
                        {row.product_id._id}
                        {", "}
                        <b>{row.product_id.product_type_id.name}</b>
                      </>
                    )}
                  </TableCell>
                  <TableCell align="center">{row.order_id || "-"}</TableCell>
                  <TableCell align="center">{row.return_id || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <ImageCarouselDialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        slides={selectedSlides}
        initialIndex={slideIndex}
        title={t("itemLists.itemImage")}
      />
      {itemsEditable && (
        <div className={classes.footer}>
          <Button variant="contained" onClick={saveItems} disabled={CUSTOMER_STATE.updateSubscriptionItemsLoading}>
            {CUSTOMER_STATE.updateSubscriptionItemsLoading ? t("itemLists.loading") : t("itemLists.save")}
          </Button>
        </div>
      )}
    </React.Fragment>
  );
}
